import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Bell, BellRing, CheckCheck, Smartphone, Laptop, ShieldAlert } from 'lucide-react';

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return iso; } };

const iconFor = (type) => {
  if (type === 'blocked_attempt') return ShieldAlert;
  return Bell;
};

export const GestoreNotificationsBell = ({ tone = 'light', idSuffix = '' }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try { const { data } = await adminApi.get('/admin/notifications/unread-count'); setUnread(data.count || 0); }
    catch (e) { /* noop */ }
  }, []);

  const loadList = useCallback(async () => {
    try { const { data } = await adminApi.get('/admin/notifications'); setItems(data); }
    catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 30000);
    return () => clearInterval(id);
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const markAllRead = async () => {
    try { await adminApi.post('/admin/notifications/read-all'); setUnread(0); loadList(); } catch (e) { /* noop */ }
  };

  const triggerColor = tone === 'dark'
    ? 'text-background/80 hover:text-background hover:bg-background/10'
    : 'text-foreground hover:bg-muted';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className={`relative ${triggerColor}`}
          data-testid={`gestore-notifications-bell${idSuffix}`}
          aria-label={t('gestore.nTitle')}
        >
          {unread > 0 ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          {unread > 0 && (
            <span
              data-testid={`gestore-notifications-badge${idSuffix}`}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[320px] p-0" data-testid={`gestore-notifications-panel${idSuffix}`}>
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="font-semibold text-sm">{t('gestore.nTitle')}</div>
          {items.some((n) => !n.read) && (
            <button
              onClick={markAllRead}
              data-testid={`gestore-notifications-mark-all${idSuffix}`}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <CheckCheck className="h-3.5 w-3.5" /> {t('gestore.nMarkAllRead')}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">{t('gestore.nEmpty')}</div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            <div className="divide-y divide-border">
              {items.map((n) => {
                const Icon = iconFor(n.type);
                const DevIcon = n.device_type === 'mobile' ? Smartphone : Laptop;
                return (
                  <div key={n.id} className={`flex gap-2.5 px-3 py-2.5 ${n.read ? '' : 'bg-primary/5'}`} data-testid={`gestore-notification-${n.id}`}>
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${n.type === 'blocked_attempt' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm leading-snug">{n.message}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        {n.device_type && <DevIcon className="h-3 w-3" />}
                        <span className="truncate">{n.restaurant_name || ''}</span>
                        <span>·</span>
                        <span className="tabular shrink-0">{fmt(n.created_at)}</span>
                      </div>
                    </div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
